import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const FAQ_ITEMS = [
  {
    question: 'Does Portsmouth\'s all-GREEN DfT rating block my pothole claim?',
    answer: 'No — but it changes your approach. Portsmouth is one of the few councils in England rated GREEN on all four DfT scorecards, which strengthens their Section 58 defence at network level. Your claim still turns on the specific defect: whether it met intervention criteria, whether it was found in a safety inspection, and whether it was repaired within reasonable timeframes — not on the colour of a city-wide scorecard.',
  },
  {
    question: 'What does all-GREEN actually mean for Portsmouth?',
    answer: 'GREEN means Portsmouth performs above the DfT threshold on overall condition, spend, and best practice. Its own transparency report shows only 1–2% of roads in RED condition across every class, and capital spend running at more than six times the DfT allocation. It does not mean zero potholes: the council filled an estimated 6,890 potholes across five years, including 1,510 in 2024/25.',
  },
  {
    question: 'Portsmouth shows just 1% RED roads — can I still claim?',
    answer: 'Yes. RED is a network survey category, not a guarantee that no individual pothole was unreasonably left unrepaired. Portsmouth\'s own data shows B and C roads shifting from 15% amber in 2020 to 21% amber in 2024 — maintenance may be required soon on a growing share of classified roads. Reactive repairs, public reports, and inspection records for your specific street decide the claim.',
  },
  {
    question: 'Who is liable — Portsmouth Council, Ensign, or Colas?',
    answer: 'Portsmouth City Council remains the highway authority under the Highways Act 1980. Maintenance is delivered through a 25-year Private Finance Initiative with Ensign Highways (Colas as subcontractor), but claims for pothole damage are made against the council. The PFI contract\'s performance standards and inspection regime are relevant evidence — they show what the council committed to, not a separate legal entity you sue instead.',
  },
  {
    question: 'Residential roads are inspected every six months — does that weaken my claim?',
    answer: 'It strengthens the council\'s Section 58 position on residential streets compared with councils on annual or multi-year cycles. Main roads and pavements in and out of Portsmouth are inspected monthly. Your claim therefore needs to show the specific defect should have been caught sooner — prior reports before your incident, photos showing visible age, or proof it met category-one safety criteria and was not repaired promptly.',
  },
  {
    question: 'Portsmouth filled 1,510 potholes in 2024/25 — does that mean the roads are fixed?',
    answer: 'No. Reactive repairs treat individual defects; they do not reset network condition. Portsmouth still projects approximately 1,400 pothole fills in 2025/26 despite four GREEN scorecards. The council\'s own report notes a gradual shift from good to fair condition on classified roads between 2021 and 2023 — defects can still form and damage vehicles between inspections.',
  },
  {
    question: 'Are the 79–80% preventative spend figures reliable?',
    answer: 'Treat them with caution. Portsmouth\'s transparency report states the PFI contract transfers maintenance risk to Ensign on a fixed-price basis, so there is no actual split between preventative and reactive spend in contract data. The council uses the Asphalt Industry Alliance ALARM survey desired allocation instead. For your claim, inspection frequency, defect response times, and prior reports matter more than an estimated spend ratio.',
  },
];

export function PortsmouthCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Portsmouth Pothole Claims | All-GREEN DfT Rating | Fixtyer"
        description="Portsmouth is one of England's few all-GREEN councils — yet filled 6,890 potholes in five years via its 25-year PFI. See what the transparency report reveals about amber roads, Section 58, and your claim."
        canonical="/councils/portsmouth"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Portsmouth', url: '/councils/portsmouth' },
        ]}
      />
      <FAQSchema faqs={FAQ_ITEMS} />
      <ServiceSchema
        councilName="Portsmouth City Council"
        councilSlug="portsmouth"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/portsmouth"
        headline="Portsmouth Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Portsmouth</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Portsmouth: 1% RED Roads, 6,890 Pothole Repairs
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Portsmouth City Council is one of the very few authorities in England rated{' '}
            <strong className="text-emerald-400">GREEN on all four DfT scorecards</strong> — the only council
            on the Hampshire and South Coast map to achieve that. Its 25-year PFI with Ensign Highways delivers
            capital spend at more than six times the DfT allocation. Yet the council&apos;s own transparency
            report still records <strong className="text-amber-400">6,890 estimated pothole repairs across five years</strong>,
            a shift from good to fair condition on classified roads, and 408km of residential streets where most
            claims actually happen. GREEN is a network verdict. Your claim lives or dies on the specific defect.
          </p>

          <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-emerald-400">1%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition — every year since 2020</div>
            <div className="text-brand-300 text-sm mt-1">
              Stable on paper across 408km of residential network — but 21% of B and C roads are now amber
              (&ldquo;maintenance may be required soon&rdquo;), up from 15% in 2020, while the council still
              filled 1,510 potholes in 2024/25 alone.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT Scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-8">
            The DfT&apos;s verdict — all four GREEN
          </h2>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full">
              <caption className="sr-only">Portsmouth City Council DfT Road Maintenance Ratings 2025-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Scorecard</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Performance</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center text-surface-600">—</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">Above DfT threshold</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">75% green (2024 survey)</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">B &amp; C roads</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">78% green (2024 survey)</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Unclassified roads</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">1% red (2020–2024)</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center text-surface-600">~6× DfT capital allocation</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Best practice</td>
                  <td className="px-6 py-4 text-center text-surface-600">PFI asset management, WMHI aligned</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <p className="text-emerald-800">
              <strong>What all-GREEN means:</strong> Portsmouth is performing above average on every DfT scorecard —
              one of a handful of councils in England to achieve that. Across Hampshire and the South Coast, the
              council states it is the only authority with GREEN in all categories on the DfT&apos;s local authority
              map. That reflects genuine long-term investment through a 25-year highways PFI running from 2005 to 2030.
            </p>
          </div>

          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-amber-800">
              <strong>Context for your claim:</strong> GREEN measures network-level performance against other councils —
              not whether the specific pothole that damaged your vehicle was reasonably inspected and repaired. Portsmouth
              still filled an estimated 6,890 potholes across five years and acknowledges a shift from good to fair
              condition on classified roads. A top rating makes generic &ldquo;the roads are terrible&rdquo; arguments
              useless. Specificity wins.
            </p>
          </div>
        </div>
      </section>

      {/* Network scale & PFI */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            505 kilometres — mostly residential, mostly PFI
          </h2>
          <p className="text-surface-600 mb-8">
            Network size from Portsmouth&apos;s own transparency report — delivered through Ensign Highways and Colas
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center font-mono">62 km</td>
                  <td className="px-6 py-4 text-center">12.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">35 km</td>
                  <td className="px-6 py-4 text-center">6.9%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-medium text-amber-900">Unclassified (U roads)</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">408 km</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">80.8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-bold text-brand-900">Total roads</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">505 km</td>
                  <td className="px-6 py-4 text-center">100%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">Footways</td>
                  <td className="px-6 py-4 text-center font-mono">256 km</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-brand-900">Cycleways</td>
                  <td className="px-6 py-4 text-center font-mono">27 km</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 25-year PFI model</h3>
              <p className="text-surface-600 text-sm mb-3">
                Unlike most authorities, Portsmouth delivers highway maintenance through a Private Finance Initiative
                with Ensign Highways (Colas as subcontractor), running from 2005 to 2030. The contract covers the
                majority of roads, footways, and cycleways, with performance standards for inspections, repairs, and
                network condition index maintenance.
              </p>
              <p className="text-surface-600 text-sm">
                Fixed-price PFI delivery means maintenance risk sits with the service company — which underpins the
                council&apos;s GREEN spend and best-practice scorecards, but does not eliminate individual pothole claims.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Inspection frequency</h3>
              <p className="text-emerald-800 text-sm mb-3">
                Colas inspects main roads and pavements in and out of Portsmouth <strong>every month</strong> (excluding
                A27/M27, which Highways England maintains). Residential roads are inspected <strong>every six months</strong>.
                Ensign carries out annual condition surveys under DfT guidelines.
              </p>
              <p className="text-emerald-800 text-sm">
                That is a stronger inspection regime than many councils — which raises the bar for your claim unless
                you can show prior notice or a defect that should have been caught sooner.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five years of condition data
          </h2>
          <p className="text-surface-600 mb-8">
            RED / amber / green percentages from Portsmouth&apos;s transparency report — SCANNER on classified roads,
            annual surveys with Ensign Highways
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (62km — 12.3% of network): stable RED, rising amber</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">78%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">77%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">77%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-amber-50">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">24%</td>
                    <td className="px-4 py-2 text-center font-bold">74%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">2%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">23%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">75%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              RED share held at 2–3% — but green A-roads fell from 78% to 75% as amber rose from 20% to 23%.
              The council describes this as a shift from good to fair condition, particularly between 2021 and 2023.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (35km — 6.9% of network): 1% RED, amber up six points</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">15%</td>
                    <td className="px-4 py-2 text-center">84%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">15%</td>
                    <td className="px-4 py-2 text-center">84%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">15%</td>
                    <td className="px-4 py-2 text-center">84%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-amber-50">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">21%</td>
                    <td className="px-4 py-2 text-center font-bold">78%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">1%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">21%</td>
                    <td className="px-4 py-2 text-center font-bold">78%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B and C roads stayed at 1% RED — but amber share rose from 15% to 21% between 2020 and 2024.
              Under DfT definitions, amber means maintenance may be required soon. That is published evidence
              the council knows a growing slice of its classified network is drifting from good toward fair.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
            <h3 className="font-semibold text-emerald-900 mb-4">Unclassified roads (408km — 80.8% of network): 1% RED every year</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-emerald-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-100 border-b border-emerald-200">
                    <th className="px-4 py-2 text-left font-semibold text-emerald-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-700">1%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-700">1%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-700">1%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-700">1%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-700">1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-emerald-800 font-medium">
              The council reports 1% RED on U-roads consistently — roughly 4km of residential network in the poorest
              survey category. That supports the GREEN condition scorecard. But most pothole claims happen on estate
              streets where six-monthly safety inspections, not network-wide SCANNER percentages, determine Section 58.
            </p>
          </div>
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the council&apos;s report acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim from Portsmouth&apos;s Local Highways Maintenance Transparency Report
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Between 2020 and 2024, the condition of A, B, and C roads remained largely stable, with only 2–3% of A roads and 1% of B and C roads classified as being in poor condition. However, a gradual shift was observed from roads in good condition to those in fair condition particularly between 2021 and 2023 highlighting an emerging need for preventative maintenance."
              source="Portsmouth City Council Local Highways Maintenance Transparency Report 2025"
            />

            <QuoteCard
              quote="Overall, Portsmouth's Highway network remains in good condition. However, the increasing proportion of roads rated as fair emphasizes the need for continued investment and proactive asset management to sustain performance and prevent further deterioration."
              source="Portsmouth City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">What this means for your claim</h3>
            <p className="text-brand-200 mb-4">
              Portsmouth is not denying potholes exist — it is documenting a network drifting from green toward amber
              on classified roads while holding RED shares low. For Section 58, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your pothole on a B or C road in the 21% amber category — maintenance may be required soon?</li>
              <li>• Did the defect meet category-one (safety-critical) or category-two intervention criteria in the PFI contract?</li>
              <li>• Was it reported before your incident — via My Portsmouth, the online form, or 023 9284 1047?</li>
              <li>• On a residential street, when was the last six-monthly Colas inspection before your damage?</li>
              <li>• Does your photo show weathered edges or previous patching — proof the defect predated your incident?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot rely on network-level GREEN ratings alone to defeat a claim about a specific, reportable
              defect it failed to repair within its own inspection and response regime.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            6,890 estimated pothole repairs in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from the council&apos;s transparency report — carriageways and footways combined
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">1,244</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">979</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,268</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">1,889</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">1,510</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">6,890</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Category one and category two defects</h3>
              <p className="text-surface-600 text-sm">
                Portsmouth&apos;s figures include category-one defects — safety-critical issues requiring immediate
                attention — and category-two defects that will likely become serious if left unaddressed, such as
                potholes less than 20mm deep expected to worsen. An all-GREEN council still processes hundreds of
                reactive orders every year because defects form on a heavily used island city network.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">~4 repairs a day, averaged over five years</h3>
              <p className="text-amber-800 text-sm">
                The council reports an average of 1,378 pothole fills per year across 505km — lower than many
                counties, but not zero. Peak year 2023/24 saw 1,889 fills. Defects still appear between monthly
                main-road and six-monthly residential inspections — which is when prior-report evidence decides claims.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">Following the money</h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            PFI-backed capital spend far exceeds DfT grants — but preventative/reactive splits are estimated, not contractual
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 p-6 mb-8 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative (est.)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£2.441m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">£9.641m</td>
                  <td className="px-4 py-3 text-center">£16.790m</td>
                  <td className="px-4 py-3 text-center">79%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£1.786m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">£9.636m</td>
                  <td className="px-4 py-3 text-center">£17.811m</td>
                  <td className="px-4 py-3 text-center">82%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£1.786m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">£10.743m</td>
                  <td className="px-4 py-3 text-center">£19.640m</td>
                  <td className="px-4 py-3 text-center">80%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£2.011m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">£13.001m</td>
                  <td className="px-4 py-3 text-center">£19.786m</td>
                  <td className="px-4 py-3 text-center">78%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£2.011m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">£12.792m</td>
                  <td className="px-4 py-3 text-center">£20.700m</td>
                  <td className="px-4 py-3 text-center">79%</td>
                </tr>
                <tr className="bg-brand-800/50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center">£2.731m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">£13.031m</td>
                  <td className="px-4 py-3 text-center">£21.455m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">80%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 mb-6">
            <div>
              <div className="text-3xl font-bold text-emerald-400">6.4×</div>
              <div className="text-brand-300 text-sm">Capital spend vs DfT allocation (2024/25)</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-400">£33.5m</div>
              <div className="text-brand-300 text-sm">Total capital + revenue spend 2024/25</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-400">4.4 km</div>
              <div className="text-brand-300 text-sm">Carriageway resurfacing planned 2025/26</div>
            </div>
          </div>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl p-6">
            <p className="text-brand-200 text-sm">
              <strong className="text-amber-400">Important caveat:</strong> Portsmouth states the PFI contract
              transfers maintenance risk to Ensign on a fixed-price basis, so there is no contractual split between
              preventative and reactive spend. The 78–82% preventative figures come from the Asphalt Industry Alliance
              ALARM survey desired allocation — not audited PFI accounts. Cite them as context, not as proof the specific
              pothole was unforeseeable.
            </p>
          </div>
        </div>
      </section>

      {/* 2025/26 plans & coastal context */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            2025/26 programme and coastal pressure
          </h2>
          <p className="text-surface-600 mb-8">
            Planned works from the council&apos;s transparency report — and environmental factors the council documents
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Planned maintenance 2025/26</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Resurfacing 4.4 km of carriageways</li>
                <li>• Upgrading 8 km of footways</li>
                <li>• Repairing three highway structures</li>
                <li>• Filling approximately 1,400 potholes</li>
              </ul>
              <p className="text-surface-500 text-sm mt-4">
                Delivery managed through the PFI contract with performance-based, whole-life asset management.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Island city resilience</h3>
              <p className="text-surface-600 text-sm mb-3">
                Portsmouth&apos;s coastal location means flooding, drainage, and coastal schemes (Southsea and North
                Portsea) intersect with highway maintenance. The council documents climate adaptation — permeable
                materials, drainage enhancement, and extreme-weather plans including 950 tonnes of road salt stored
                at Farlington.
              </p>
              <p className="text-surface-600 text-sm">
                Water ingress and freeze-thaw cycles accelerate pothole formation. If your incident followed heavy
                rain or cold weather, timing relative to inspections and gritting routes may be relevant context.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="When residents come forward to report potholes, it makes a real difference in keeping our roads at the top quality we strive for."
            source="Portsmouth City Council — National Pothole Day statement, 2025"
          />
        </div>
      </section>

      {/* Balanced assessment */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an all-GREEN council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Portsmouth is not Derbyshire — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> All four DfT scorecards GREEN — among England&apos;s best-rated authorities
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 25-year PFI with contractual performance standards and annual condition surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Monthly main-road inspections, six-monthly residential inspections
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 1% RED on U-roads consistently — strongest survey data in the transparency report
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Capital spend at more than six times DfT allocation
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence. Generic &ldquo;council neglect&rdquo; arguments will fail.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 6,890 estimated pothole repairs in five years — defects still form
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C amber share up from 15% to 21% — council acknowledges fair-condition drift
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A-road green fell from 78% to 75% as amber rose to 23%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council explicitly encourages prior pothole reports — proof of notice matters
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Six-month gap between residential inspections — room for defects to form unnoticed
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with all-GREEN scorecards and a 25-year PFI, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong> under Section 41 and Section 58:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — My Portsmouth app, online form, or 023 9284 1047</li>
              <li>• Photos showing defect size, depth, and visible age (weathered edges, previous patching)</li>
              <li>• Whether the pothole met category-one or category-two PFI intervention criteria</li>
              <li>• Road class — residential six-monthly cycle vs monthly main-road inspection</li>
              <li>• Location on a B/C road in the 21% amber band — maintenance may be required soon</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and Portsmouth&apos;s own transparency
              data cited where it helps — without pretending an all-GREEN PFI city is the same as a failing authority.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Portsmouth first
          </h2>
          <p className="text-surface-600 mb-6">
            Portsmouth&apos;s transparency report and National Pothole Day statement both encourage residents to
            report defects promptly. Colas prioritises and repairs problems found on monthly and six-monthly
            inspections plus public reports. Reporting creates a dated record — useful evidence if the pothole was
            reported before your incident, or if the council failed to repair it within a reasonable time.
          </p>
          <a
            href="https://my.portsmouth.gov.uk/en/AchieveForms/?form_uri=sandbox-publish%3A%2F%2FAF-Process-0ee19f0f-ecae-4183-bce6-6e2d969b7a9d%2FAF-Stage-7ada4ede-9faa-4fa3-a82f-9eb82611ec9d%2Fdefinition.json"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Portsmouth City Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Also available via the My Portsmouth app or by calling 023 9284 1047. Keep your reference number and any confirmation.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Portsmouth?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            An all-GREEN PFI council demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• Ignore all-GREEN Section 58 strength</li>
                <li>• No PFI inspection-regime argument</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ All-GREEN rating acknowledged honestly</li>
                <li>✅ 6,890 five-year pothole total cited</li>
                <li>✅ B/C amber drift and fair-condition shift argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Portsmouth</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=portsmouth"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Portsmouth claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {FAQ_ITEMS.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.portsmouth.gov.uk/services/parking-roads-and-travel/transport-strategies-policies-and-funds/local-highways-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Portsmouth City Council Local Highways Maintenance Transparency Report (2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
